import { useId, useRef, type ReactNode } from "react";
import { Delete } from "lucide-react";
import { Button } from "../shadcn/Button";
import { Input } from "../shadcn/Input";
import { FormField } from "./FormField";
import { cn } from "@/shared/utils/cn";

export interface AmountInputProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
  children?: ReactNode;
  className?: string;
}

export function AmountInput({ label, value, onValueChange, error, children, className }: AmountInputProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const enterKey = (key: string) => {
    const input = inputRef.current;
    if (!input) return;
    let start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? start;
    let text = key === "backspace" ? "" : key;
    if (key === "backspace" && start === end) start = Math.max(0, start - 1);
    if (key === "." && (value.slice(0, start) + value.slice(end)).includes(".")) return;
    if (key === "." && start === 0) text = "0.";
    onValueChange(value.slice(0, start) + text + value.slice(end));
    requestAnimationFrame(() => {
      input.focus({ preventScroll: true });
      input.setSelectionRange(start + text.length, start + text.length);
    });
  };

  return (
    <div className={cn("mx-auto w-full max-w-[420px] space-y-1.5", className)}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
        <FormField id={id} label={label} error={error}>
          <Input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={value}
            onChange={(event) => onValueChange(event.target.value)}
            onFocus={(event) => event.currentTarget.select()}
            className="text-2xl font-bold"
          />
        </FormField>
        <Button
          variant="outline"
          className="mt-[27px]"
          aria-label="Clear amount"
          onClick={() => {
            onValueChange("");
            inputRef.current?.focus({ preventScroll: true });
          }}
        >
          Clear
        </Button>
      </div>
      {children}
      <div role="group" aria-label="Amount keypad" className="grid grid-cols-3 gap-1.5">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "backspace"].map((key) => (
          <Button
            key={key}
            variant="outline"
            aria-label={key === "." ? "Decimal point" : key === "backspace" ? "Backspace" : undefined}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => enterKey(key)}
            className="px-2 font-semibold"
          >
            {key === "backspace" ? <Delete aria-hidden="true" /> : key}
          </Button>
        ))}
      </div>
    </div>
  );
}
